import React from "react";
import { BookingStatus } from "@/types/trip";
import { Tooltip } from "@heroui/tooltip";

interface BookingStatusButtonProps {
  status: BookingStatus;
  onStatusChange: (newStatus: BookingStatus) => void;
  type?: "transport" | "activity";
  className?: string;
  disabled?: boolean;
  compact?: boolean;
  showStatus?: boolean;
  toolTipPlacement?: "top" | "bottom" | "left" | "right";
}

const statusColors = {
  booked: "bg-green-500/10 text-green-600 hover:bg-green-500/20",
  pending: "bg-yellow-500/10 text-yellow-600 hover:bg-yellow-500/20",
  cancelled: "bg-red-500/10 text-red-600 hover:bg-red-500/20",
  not_booked: "bg-gray-500/10 text-gray-600 hover:bg-gray-500/20",
} as const;

const statusOrder = ["not_booked", "pending", "booked", "cancelled"] as const;
type StatusType = (typeof statusOrder)[number];

const StatusIcon: React.FC<{ status: BookingStatus; className?: string }> = ({
  status,
  className = "w-4 h-4",
}) => {
  switch (status) {
    case "booked":
      return <i className={`fa-solid fa-circle-check ${className}`} />;
    case "pending":
      return <i className={`fa-solid fa-clock ${className}`} />;
    case "cancelled":
      return <i className={`fa-solid fa-circle-xmark ${className}`} />;
    case "not_booked":
      return <i className={`fa-solid fa-circle-question ${className}`} />;
  }
};

const formatStatus = (status: BookingStatus) => {
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const BookingStatusButton: React.FC<BookingStatusButtonProps> = ({
  status,
  onStatusChange,
  type = "activity",
  className = "",
  disabled = false,
  compact = false,
  showStatus = false,
  toolTipPlacement = "top",
}) => {
  const handleClick = () => {
    if (disabled) return;
    const currentIndex = statusOrder.indexOf(status as StatusType);
    const nextIndex = (currentIndex + 1) % statusOrder.length;
    const nextStatus = statusOrder[nextIndex];
    onStatusChange(nextStatus as BookingStatus);
  };

  if (compact) {
    return (
      <Tooltip
        closeDelay={0}
        delay={500}
        showArrow
        radius="full"
        placement={toolTipPlacement}
        content={
          <p className="text-xs font-light p-1">Click to update status</p>
        }
      >
        <button
          onClick={handleClick}
          disabled={disabled}
          className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 transform active:scale-95 focus:outline-none ${
            statusColors[status]
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
        >
          <StatusIcon status={status} />
        </button>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      closeDelay={0}
      delay={500}
      showArrow
      radius="full"
      content={<p className="text-xs font-light p-1">Click to update status</p>}
    >
      <button
        onClick={handleClick}
        disabled={disabled}
        className={`inline-flex items-center justify-center ${
          showStatus ? "px-3 space-x-2" : "w-8"
        } h-8 rounded-full transition-all duration-200 transform active:scale-95 focus:outline-none ${
          statusColors[status]
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
      >
        <StatusIcon status={status} />
        {showStatus && (
          <span className="text-xs font-normal whitespace-nowrap">
            {formatStatus(status)}
          </span>
        )}
      </button>
    </Tooltip>
  );
};
